"use client";
import Link from "next/link";
import { loginSchema } from "@/utils/validation/SignupSchema";
import { InputField, PasswordField } from "@/components/ui";
import { Formik, Form } from "formik";
import { signIn, useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { Loader } from "@/components/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { showToast } from "@/app/CustomToastProvider";
import { LOGIN_EVENT, trackEvent } from "@/utils/gtm";
import { Turnstile, type TurnstileHandle } from "@/components/ui";


interface LoginFormValues {
  email: string;
  password: string;
  login_via: string;
}
interface LoginProps {
  onSuccess?: () => any;
  onSignupClick?: () => any;
}
const LoginForm = (props: LoginProps) => {
  const currentUrl = usePathname();
  const isClient = typeof window !== "undefined";
  const params = currentUrl === "/login" 
  ? "/dashboard/db-booking" 
  : (isClient ? window.location.href : "/dashboard/db-booking"); // Ensure a valid fallback URL

  // const params: any  = (currentUrl == "/login") ? "/dashboard/db-booking" : (typeof window !== "undefined" && window?.location?.href);
  const [isLoading, setIsLoading] = useState(false); 
  const router = useRouter();
  const initialValues: LoginFormValues = {
    email: "",
    password: "",
    login_via: "web",
  };

  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const turnstileRef = useRef<TurnstileHandle>(null);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken("");
    turnstileRef.current?.reset();
  }, []);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    resetTurnstile();
    showToast("CAPTCHA verification failed. Please try again.", "danger");
  }, [resetTurnstile]);

  const handleTurnstileExpire = useCallback(() => {
    resetTurnstile();
    showToast("CAPTCHA expired. Please verify again.", "danger");
  }, [resetTurnstile]);

  const onSubmit = async (data: LoginFormValues) => {
   
    if (!turnstileToken) {
      showToast('Please complete the CAPTCHA','danger');
      return;
    }
    const callbackUrl = params && params.startsWith("/") ? `${window.location.origin}${params}` : params;

    setIsLoading(true); 
    try {
      const result = await signIn("roave-signin", {
        redirect: false,
        ...data,
        turnstile_challenge_token: turnstileToken,
        ...(params != null && { callbackUrl: callbackUrl })
      });
     
      // console.log('onSubmit result',result, params);
      if (result?.error) {
        showToast(result?.error, "danger");
        setIsLoading(false);
        resetTurnstile();
      } else {
        // console.log("else", result)
        setIsLoading(false); 
        props.onSuccess && props.onSuccess();
        trackEvent(LOGIN_EVENT, {
          method:"email",
          email: data?.email,
        });
        // console.log("else", params)
        resetTurnstile();
        router.push(params); // Redirect to a protected route
        if(currentUrl === "/hotels" && isClient){
          window.location.reload();
        }
      }
    } catch (error) {
      setIsLoading(false);
      resetTurnstile();
      console.error("Error during login:", error);
    }
  };

  useEffect(() => {
    if (!turnstileToken) return;

    const resetTimeout = setTimeout(() => {
      resetTurnstile();
      showToast("CAPTCHA expired. Please verify again.", "danger");
    }, 120000);

    return () => clearTimeout(resetTimeout);
  }, [turnstileToken, resetTurnstile]);

  return (
    <>
    {isLoading && <Loader show={isLoading} />}
   
      
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={loginSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form className="row y-gap-20">
         {/* {JSON.stringify(turnstileToken)} */}

          <div className="col-12">
            {currentUrl == "/login" && <h1 className="text-22 fw-500">Welcome back</h1>}
            <p className="mt-10">
              Don&apos;t have an account yet?{" "}
              {currentUrl == "/login" ? <Link href="/signup" className="text-blue-1">
                Sign up for free
              </Link>: <button type="button" onClick={() => props.onSignupClick && props.onSignupClick()}  className="text-blue-1">Sign up for free</button>}
            </p> 
          </div>
        
          {/* ss{JSON.stringify(params)} */}
          <div className="col-12">
            <InputField name="email" label="Email" type="email" />
          </div>
          

          <div className="col-12">
            <PasswordField name="password" label="Password" />
          </div>
        

          <div className="col-12">
            <Link href="/forgot-password" className="text-14 fw-500 text-blue-1 underline">
              Forgot your password?
            </Link>
          </div>
          <div className="col-12">
          <Turnstile
              ref={turnstileRef}
              onVerify={handleTurnstileVerify}
              onError={handleTurnstileError}
              onExpire={handleTurnstileExpire}
              siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
            />
            </div>
          <div className="col-12">
            <button
              type="submit"
              className="button py-20 -dark-1 bg-blue-1 text-white w-100"
            >
              Sign In 
              {/* <div className="icon-arrow-top-right ml-15" /> */}
            </button>
          </div>
          {/* End .col */}
        </Form>
      )}
    </Formik>
    </>
  );
};

export default LoginForm;
